import { useMemo } from "react";

import { formatNumber, truncateToDecimals } from "@/utils/numberFormat";
import { formatDate } from "@/utils/dateFormatter";
import { formatNameWithTooltip } from "@/utils/nameFormatter";
import { getUnitLabel } from "@/utils/unitFormatter";
import { convertValue } from "@/utils/unitConverter";

import { QUANTITY_DECIMALS } from "@/components/global.constants";
import useFilterStore from "@/stores/useFilterStore";

import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomProductName from "@/components/desktop/products/product_name/CustomProductName";

import { getMovementTypeLabel, getMovementTypeVariant } from "@/constants/movementTypes";

export function useUsageHistoryColumns(tableState, { hasIntegration = false, onIntegrationClick = null } = {}) {
	const { filter } = useFilterStore();
	const unitMeasurement = filter?.unit_measurement || "kg_l";

	return useMemo(() => {
		const columns = [
			{
				Header: "#",
				accessor: "",
				disableSortBy: true,
				width: "3%",
				Cell: ({ row }) => {
					return <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>;
				},
			},
			{
				Header: "Fecha Registro",
				accessor: "created_date",
				width: "8%",
				Cell: ({ value }) => formatDate(value),
			},
			{
				Header: "Producto",
				accessor: "product_name",
				width: "11%",
				Cell: ({ value, row }) => {
					if (!value) return <span>-</span>;
					return (
						<CustomProductName
							product={{
								code: row.original.product_code,
								value: row.original.product_id,
								label: value,
							}}
						/>
					);
				},
			},
			{
				Header: "Cant. Utilizada",
				accessor: "usage_quantity",
				width: "9%",
				meta: { cellClassName: "tdRight" },
				Cell: ({ value, row }) => {
					if (row.original.unit_measurement === "u") {
						return formatNumber(value, 0);
					}
					const convertedValue = convertValue(value, row.original.unit_measurement, unitMeasurement);
					return (
						<div style={{ fontWeight: 500 }}>
							{formatNumber(truncateToDecimals(convertedValue, QUANTITY_DECIMALS), QUANTITY_DECIMALS) +
								" " +
								getUnitLabel(row.original.unit_measurement, unitMeasurement)}
						</div>
					);
				},
			},
			{
				Header: "Costo",
				accessor: "cost_local",
				width: "8%",
				meta: { cellClassName: "tdRight" },
				Cell: ({ value }) => {
					return <span>{formatNumber(value, 2)}</span>;
				},
			},
			{
				Header: "N° OA",
				accessor: "application_order_code",
				width: "11%",
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;

					const codes = value.split(",").map((code) => code.trim());

					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{codes.map((code, index) => (
								<CustomBadge key={index} variant="neutral" size="sm">
									{code}
								</CustomBadge>
							))}
						</div>
					);
				},
			},
			{
				Header: "Centro Costo",
				accessor: "business_center_name",
				width: "16%",
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;

					const centers = value.split(",").map((center) => center.trim());

					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{centers.map((center, index) => (
								<CustomBadge key={index} variant="neutral" size="sm">
									{center}
								</CustomBadge>
							))}
						</div>
					);
				},
			},
			{
				Header: "Bodega Origen",
				accessor: "estate_parent_origin_name",
				width: "13%",
				Cell: ({ value, row }) => {
					const originName = row.original.estate_origin_name || "";
					const parentOriginName = value || "";

					if (originName === parentOriginName) {
						const { abbreviated, full } = formatNameWithTooltip(originName);
						return <CustomTooltip text={abbreviated} description={full} />;
					}

					const displayText = parentOriginName && originName ? `${parentOriginName} - ${originName}` : parentOriginName || originName;
					const { abbreviated, full } = formatNameWithTooltip(displayText);
					return <CustomTooltip text={abbreviated} description={full} />;
				},
			},
			{
				Header: "Tipo",
				accessor: "type_movement",
				width: "10%",
				meta: { cellClassName: "tdCenter" },
				Cell: ({ value }) => {
					const label = getMovementTypeLabel(value);
					const variant = getMovementTypeVariant(value);

					return (
						<CustomBadge variant={variant} size="sm">
							{label}
						</CustomBadge>
					);
				},
			},
		];

		if (hasIntegration) {
			const buildIntegrationTooltipDescription = (outputCode, responseMessage) => (
				<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
					{outputCode ? (
						<div>
							<strong>Código salida:</strong> {outputCode}
						</div>
					) : null}
					<div>
						<strong>Respuesta:</strong> {responseMessage}
					</div>
				</div>
			);

			const renderIntegrationBadge = (status, index, row) => {
				const responseMessage = status.message || (status.completed === 1 ? "Sincronizado correctamente" : "No se pudo sincronizar");
				const tooltipDescription = buildIntegrationTooltipDescription(row.original.output_code, responseMessage);
				const handleClick = onIntegrationClick
					? () =>
							onIntegrationClick({
								code: row.original.output_code,
								integrationId: status.integration_id,
								balanceId: row.original.balance_id,
							})
					: undefined;
				return (
					<CustomTooltip
						key={index}
						text={
							<div onClick={handleClick} style={{ cursor: onIntegrationClick ? "pointer" : "default" }}>
								<CustomBadge variant={status.completed === 1 ? "info" : "danger"} size="sm">
									{status.completed === 1 ? "✓" : "✗"} {status.name}
								</CustomBadge>
							</div>
						}
						description={tooltipDescription}
					/>
				);
			};

			columns.push({
				Header: "Integración",
				id: "integration_statuses",
				accessor: "integration_statuses",
				disableSortBy: true,
				width: "8%",
				Cell: ({ value, row }) => {
					if (!value || value.length === 0) return <span>-</span>;
					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{value.map((status, index) => renderIntegrationBadge(status, index, row))}
						</div>
					);
				},
			});
		}

		columns.push({
			Header: "",
			accessor: "_expand",
			disableSortBy: true,
			width: "3%",
			Cell: () => null,
		});

		return columns;
	}, [tableState, unitMeasurement, hasIntegration, onIntegrationClick]);
}
